<template>
	<div :class="classes" ref="root">
		<font-awesome-icon
			:icon="icon"
			class="mr-2"
			fixed-width
			v-if="icon"
		/>
		<input
			:type="inputType"
			:value="modelValue"
			:placeholder="placeholder"
			:id="inputId"
			@keydown="textboxKeydown"
			@keyup="textboxKeyup"
			@input="input"
			:tabindex="tabindex"
			:disabled="disabled"
			:readonly="readonly"
			:aria-label="ariaLabel"
			:aria-describedby="ariaDescribedBy"
			ref="input"
		/>
		<slot name="tail" />
	</div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-facing-decorator';

import { CSSClasses } from '@/common/types';

export declare type HdlTextboxStyle = 'auth' | 'default' | 'plain' | 'secondary' | 'search' | 'heading' | 'no-border' | 'action-menu';

@Component({
	emits: [
		'update:modelValue', 'enter', 'key-up-out', 'key-down-out', 'escape', 'keydown'
	]
})
export default class HdlTextbox extends Vue {
	@Prop({ default: '' })
	modelValue: string;

	@Prop({ default: null })
	icon: [string, string] | null;

	@Prop({ default: false, type: Boolean })
	password: boolean;

	@Prop({ default: null })
	inputId: string | null;

	@Prop({ default: '' })
	placeholder: string;

	@Prop({ default: 'default' })
	hdlStyle: HdlTextboxStyle;

	@Prop({ default: 0 })
	tabindex: number;

	@Prop({ default: false, type: Boolean })
	focusOnMount: boolean;

	@Prop({ default: false, type: Boolean })
	disabled: boolean;

	@Prop({ default: false, type: Boolean })
	readonly: boolean;

	@Prop({ default: false, type: Boolean })
	captureKeys: boolean;

	@Prop({ default: null })
	ariaLabel: string | null;

	@Prop({ default: null })
	ariaDescribedBy: string | null;

	mounted(): void {
		if (this.focusOnMount) {
			this.focus();
		}
	}

	textboxKeyup(event: KeyboardEvent): boolean {
		if (event.key === 'Enter') {
			this.$emit('enter', event);
			event.preventDefault();
			return true;
		} else if (event.key === 'Escape') {
			this.$emit('escape', event);
			event.preventDefault();
			return true;
		}

		return false;
	}

	textboxKeydown(event: KeyboardEvent): boolean {
		if (this.captureKeys) {
			event.stopPropagation();
		}

		if (event.key === 'ArrowDown') {
			this.$emit('key-down-out');
			event.preventDefault();
			return true;
		} else if (event.key === 'ArrowUp') {
			this.$emit('key-up-out');
			event.preventDefault();
			return true;
		}

		return false;
	}

	input(event: InputEvent): void {
		if (event.currentTarget === null) return;

		const input = (event.currentTarget as HTMLInputElement);

		// if (this.debouncedInput !== null) this.debouncedInput();

		this.$emit('update:modelValue', input.value);
	}

	focus(): void {
		const textbox = this.$refs.input as HTMLInputElement;
		textbox.focus();
	}

	blur(): void {
		const textbox = this.$refs.input as HTMLInputElement;
		textbox.blur();
	}

	get inputType(): string {
		return (this.password)? 'password' : 'text';
	}

	get classes(): CSSClasses {
		const classes: CSSClasses = {
			'hdl-textbox': true,
			'disabled': this.disabled,
			'input-box-borders': this.hdlStyle === 'default' || this.hdlStyle === 'auth'
		};

		classes[this.hdlStyle] = true;

		return classes;
	}
}
</script>

<style lang="scss">
.hdl-textbox {
	@apply rounded flex flex-row items-center;

	> input {
		@apply rounded w-full bg-fill;

		outline: none;

		&::placeholder {
			@apply text-disabled;
		}
	}

	&.disabled {
		@apply text-disabled;
	}

	&.default {
		@apply bg-fill;

		padding: .35rem .5rem;

		&.disabled {
			@apply text-disabled bg-fill-gray;

			> input:disabled {
				@apply bg-fill-gray;
			}
		}

		&:focus-within {
			--tw-border-opacity: 1;
			border: 1.5px solid var(--main);
			box-shadow: #535abb25 0 0 0 2px;
		}

		&:focus-within,
		&:hover {
			* {
				outline: none;
			}
		}
	}

	&.no-border {
		@apply p-1 border-0;

		&:focus-within {
			@apply border-0;

			> input {
				outline: none;
			}
		}
	}

	&.plain {
		@apply border p-2;

		&:focus-within {
			//border-color: var(--text-primary);
		}

		border: 1px solid rgba(var(--mainRGB), .15);
		box-shadow: rgba(0,0,0, .05) 0 2px 2px, rgba(0,0,0, .02) 0 -1px 2px;

		&:focus-within,
		&:hover {
			* {
				outline: none;
			}
			border: 1px solid rgba(var(--mainRGB), .25);
			//box-shadow: rgba(0,0,0, .1) 0 2px 2px;
		}

		&.selected {
			border-width: 1.5px;
			border-color: var(--main);
			box-shadow: rgba(0,0,0, .1) 0px 0px 0px 2px;
		}

		> input {
			border-radius: 0;
		}

		&.disabled {
			@apply text-disabled bg-fill-gray;

			> input:disabled {
				@apply bg-fill-gray;
			}
		}
	}

	&.secondary {
		@apply py-2 px-2 bg-fill-gray;

		> input {
			@apply bg-fill-gray;
		}
	}

	&.heading {
		@apply py-1 px-2 text-xl;

		&:focus-within {
			@apply border-transparent;
		}

		> input {
			@apply bg-transparent;
		}

		font-weight: 500;
	}

	&.auth {
		@apply py-2 px-2 text-lg;
	}

	&.search {
		@apply bg-fill-gray rounded-md py-2 pl-2 pr-3;

		> svg {
			@apply text-disabled;
		}

		> input {
			@apply bg-fill-gray;
		}
	}

	&.action-menu {
		@apply border border-border-gray p-1 bg-fill;

		&:focus-within {
			//border-color: var(--text-primary);
		}

		> input {
			border-radius: 0;
		}

		&.disabled {
			@apply text-disabled bg-fill-gray;

			> input:disabled {
				@apply bg-fill-gray;
			}
		}
	}
}
</style>
